import React, { ReactNode } from 'react';
import { Button } from 'components/common/Button';
import { FormErrorMessage } from 'components/common/form-utils/form-elements';
import { FieldState, FormState } from 'components/common/form-utils/form-state';
import { Voucher, VoucherType, VoucherUtils } from 'utils/Voucher';
import VoucherFieldEditor from './VoucherFieldEditor';

const FieldEditorRow = ({
  title, fieldName, field, baseVoucher, display,
}: {
  title?: string,
  fieldName: keyof Voucher,
  field: FieldState
  baseVoucher: Voucher,
  display?: VoucherType
}) => {
  const isEditing = field.value !== undefined;

  return <tr id={`voucher-${fieldName}-row`} className='h-12'>
    <td className='align-middle'>
      <label htmlFor={`voucher-${fieldName}-row`}>{title || (fieldName[0].toUpperCase() + fieldName.slice(1))}</label>
    </td>

    <td aria-label={`Current value of field ${fieldName}`}>
      {(() => {
        const labelProp = { 'aria-label': `Current value of field ${field}` };
        if (fieldName === 'isValid') {
          return <input {...labelProp} type='checkbox' readOnly checked={baseVoucher[fieldName]} />;
        }
        let displayValue: string;
        if (typeof display === 'string') displayValue = VoucherUtils.formatVoucherDiscount(baseVoucher);
        else displayValue = baseVoucher[fieldName].toString();
        return <span {...labelProp}>{displayValue}</span>;
      })()}
    </td>

    <td className='grid justify-items-center mt-1'>
      {!isEditing && <Button
        data-locator='init-edit-button' className='w-16' primary
        onClick={() => field.setValue(baseVoucher[fieldName].toString())}>Edit</Button>}
      {isEditing && <Button data-locator='cancel-edit-button' className='w-16' danger onClick={() => {
        field.setValue(undefined);
        field.setError(undefined);
      }}>Cancel</Button>}
    </td>

    <td className='text-center pt-1'>
      {!isEditing && <span className='italic text-gray-400'>Unedited</span>}
      {isEditing && <VoucherFieldEditor fieldName={fieldName} field={field} />}
      {field.error && <FormErrorMessage>{field.error}</FormErrorMessage>}
    </td>
  </tr>;
};

const VoucherEditorTable = (
  { baseVoucher, VoucherEdits }: { baseVoucher: Voucher, VoucherEdits: FormState },
) => {
  const ColumnHeader = ({ children, className }: { children: ReactNode, className?: string }) => (
    <th
      scope='col'
      className={
        `px-2 py-1 md:px-6 md:py-3 text-xs md:text-s text-center text-gray-500 uppercase tracking-wider ${className}`
      }>{children}</th>
  );

  return <table className='divide-y divide-gray-200 mt-4 mb-8'>
    <thead className='bg-gray-50'>
      <tr>
        <ColumnHeader className='w-28'>Field</ColumnHeader>
        <ColumnHeader className='w-48'>Current Value</ColumnHeader>
        <ColumnHeader className='w-24'>Edit</ColumnHeader>
        <ColumnHeader className='w-56'>Modified Value</ColumnHeader>
      </tr>
    </thead>
    <tbody className='text-center'>
      <FieldEditorRow baseVoucher={baseVoucher} title='Voucher Code' fieldName='code' field={VoucherEdits.code} />
      <FieldEditorRow
        baseVoucher={baseVoucher}
        title='Discount Type'
        fieldName='discountType'
        field={VoucherEdits.discountType} />
      {baseVoucher.discountType === 'Percentage'
        && <FieldEditorRow
          baseVoucher={baseVoucher}
          title='Discount'
          fieldName='discountValue'
          field={VoucherEdits.percentageDiscountValue}
          display={baseVoucher.discountType} />}
      {baseVoucher.discountType === 'Fixed'
        && <FieldEditorRow
          baseVoucher={baseVoucher}
          title='Discount'
          fieldName='discountValue'
          field={VoucherEdits.fixedDiscountValue}
          display={baseVoucher.discountType} />}
      <FieldEditorRow baseVoucher={baseVoucher} title='Validity' fieldName='isValid' field={VoucherEdits.isValid} />
    </tbody>
  </table>;
};

export default VoucherEditorTable;
